import type { FloraCatalog } from '@/flora/FloraCatalog';
import type { MapFrame } from '@/geo/MapFrame';
import { SitePlanFormatError } from './SitePlan';
import {
  SitePlanError,
  callVisionModel,
  extractJson,
  type ReferencePrompt,
  type VisionCallResult,
} from './SitePlanClient';
import { buildStagePrompt } from './SitePipelinePrompts';
import type { ReferenceTag } from './ReferenceTag';
import { forgeTexture } from '@/texture/TextureForge';
import { SurfacePaletteFullError } from '@/terrain/TerrainSurface';
import {
  PIPELINE_STAGE_IDS,
  parseStageArtifact,
  type PipelineArtifacts,
  type PipelineStageId,
  type SurfaceArtifact,
} from './SitePipeline';

/**
 * Runs the five reconstruction stages in order, keeping everything each one produced.
 *
 * The runner exists to make the pipeline *inspectable*, which is a stronger requirement than making
 * it work. Every stage keeps the exact prompt it was sent, the exact text that came back, how long
 * it took, what it cost in tokens, and the parsed artifact — so when a site comes out wrong the
 * question "which judgement failed" has an answer rather than a guess. Nothing is discarded on
 * success and nothing is discarded on failure either: a stage that returned unparseable JSON still
 * shows what it returned, which is usually enough to see why.
 *
 * Three properties the UI depends on:
 *
 * - **Resumable.** `run(from)` re-runs one stage and everything after it, because a changed artifact
 *   invalidates its dependents but not its dependencies. `runStage` re-runs exactly one, for when
 *   you want to see whether a stage is stable across samples.
 * - **Abortable mid-run.** One `AbortController` covers the whole sequence; cancelling leaves
 *   completed stages intact and marks the rest pending.
 * - **Observable.** `onChange` fires on every state transition, so the inspector renders progress as
 *   it happens rather than at the end of a two-minute run.
 */

export type StageStatus = 'pending' | 'running' | 'done' | 'failed' | 'cancelled';

/** One material the surfaces stage asked for, and what became of the request. */
export interface ForgedMaterialReport {
  /** The id the stage used. */
  requestedId: string;
  /** The id the palette actually assigned, absent when the forge failed. */
  layerId?: string;
  name: string;
  /** Swatch average colour, for the inspector's chip. */
  color?: string;
  /** How grounded it ended up being — "researched, rendered" / "guessed, drawn". */
  detail?: string;
  error?: string;
}

export interface StageRun {
  id: PipelineStageId;
  status: StageStatus;
  /** Materials forged for this stage. Only ever set on `surfaces`. */
  materials?: ForgedMaterialReport[];
  /** Exactly what was sent. Held so the inspector can show it before and after the call. */
  prompt?: ReferencePrompt;
  /** Exactly what came back, parsed or not. */
  raw?: string;
  artifact?: unknown;
  model?: string;
  usage?: { promptTokens: number; completionTokens: number };
  /** Wall-clock milliseconds the call took. */
  durationMs?: number;
  error?: string;
}

export interface PipelineState {
  stages: StageRun[];
  /** True while any stage is running. */
  busy: boolean;
}

/**
 * A stage update. Unlike `Partial<StageRun>` this admits an explicit `undefined`, which is how a
 * re-run clears the previous attempt's output; `pruneUndefined` then drops the key entirely.
 */
type StagePatch = { [K in keyof StageRun]?: StageRun[K] | undefined };

export interface SitePipelineRunnerDeps {
  catalog: FloraCatalog;
  apiKey: () => string | null;
  model: () => string;
  images: () => readonly Blob[];
  direction: () => string;
  /**
   * Semantic tags placed on the reference photograph. Every stage is sent them, and none of them
   * waits for any: tagging is worth a great deal and is nobody's precondition, so a run with an
   * empty list is a legitimate run that will simply be working from less.
   */
  tags?: () => readonly ReferenceTag[];
  /**
   * The map frame the reference was captured from, when it came from one. Handed to every stage so
   * the extent is quoted as measured rather than estimated.
   */
  frame?: () => MapFrame | null;
  /** Injected for tests. Defaults to the real vision call. */
  call?: typeof callVisionModel;
  /** Injected for tests. Defaults to the real texture forge. */
  forge?: typeof forgeTexture;
  /**
   * Whether the surfaces stage may have new ground materials made for it. Default on.
   *
   * Off is the "reconstruct this site without spending anything more" setting: the stage still
   * declares what it needs and the inspector still shows the declarations, but nothing is forged and
   * the regions that named a missing material are dropped by the compiler with a warning naming it.
   */
  forgeMaterials?: () => boolean;
  /** Injected for tests, so a run can be timed deterministically. */
  now?: () => number;
}

/** Stages that produce a lot of geometry need more room than a single plan did. */
const STAGE_OUTPUT_TOKENS: Record<PipelineStageId, number> = {
  segment: 16000,
  terrain: 4000,
  surfaces: 12000,
  flora: 12000,
  structures: 12000,
};

export class SitePipelineRunner {
  private stages: StageRun[] = PIPELINE_STAGE_IDS.map((id) => ({ id, status: 'pending' }));
  private listeners: (() => void)[] = [];
  private abort: AbortController | null = null;
  private running = false;

  constructor(private readonly deps: SitePipelineRunnerDeps) {}

  get state(): PipelineState {
    return { stages: this.stages.map((stage) => ({ ...stage })), busy: this.running };
  }

  /** Artifacts of every stage that has completed, keyed by stage. */
  get artifacts(): Partial<PipelineArtifacts> {
    const out: Partial<PipelineArtifacts> = {};
    for (const stage of this.stages) {
      if (stage.status !== 'done' || stage.artifact === undefined) continue;
      // Each stage's parser produced exactly the type its key names, so the cast is the one place
      // that per-stage knowledge is re-stated rather than re-derived.
      (out as Record<string, unknown>)[stage.id] = stage.artifact;
    }
    return out;
  }

  /** True once every stage has produced an artifact. */
  get complete(): boolean {
    return this.stages.every((stage) => stage.status === 'done');
  }

  onChange(listener: () => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((entry) => entry !== listener);
    };
  }

  /** The prompt a stage would be sent right now, for previewing before anything is spent. */
  previewPrompt(id: PipelineStageId): ReferencePrompt {
    const frame = this.deps.frame?.() ?? null;
    return buildStagePrompt({
      stage: id,
      catalog: this.deps.catalog,
      artifacts: this.artifacts,
      direction: this.deps.direction(),
      tags: this.tags,
      ...(frame ? { frame } : {}),
      imageCount: Math.max(1, this.deps.images().length),
    });
  }

  /** The reference tags, or none when the host has not wired any. */
  private get tags(): readonly ReferenceTag[] {
    return this.deps.tags?.() ?? [];
  }

  /**
   * Run `from` and every stage after it.
   *
   * Dependents are reset to pending first, so the inspector never shows a stale stage 4 sitting
   * under a stage 1 that has just changed underneath it.
   *
   * **Supplied stages are stepped over, not reset.** Importing a DEM or a set of surveyed footprints
   * and then pressing "run all" used to spend a call replacing the measurement with a guess at it,
   * silently — the measured artifact went back to pending along with everything else and a model
   * answered the stage again. A supplied artifact outranks anything a model would produce for the
   * same stage; {@link runStage} is how you deliberately override one.
   */
  async run(from: PipelineStageId = 'segment'): Promise<void> {
    if (this.running) return;
    const start = PIPELINE_STAGE_IDS.indexOf(from);
    if (start < 0) return;

    const queue = PIPELINE_STAGE_IDS.slice(start).filter((id) => !this.isSupplied(id));
    if (queue.length === 0) return;
    for (const id of queue) this.patch(id, { status: 'pending', error: undefined });
    await this.execute(queue);
  }

  /** Re-run exactly one stage, leaving its dependents' existing artifacts alone. */
  async runStage(id: PipelineStageId): Promise<void> {
    if (this.running) return;
    await this.execute([id]);
  }

  cancel(): void {
    this.abort?.abort();
  }

  /**
   * Supply a stage's artifact instead of asking a model for it.
   *
   * The case this exists for is measured topography: an imported DEM answers stage 2's question
   * better than any reading of a photograph can, and once it is in place the stage should not be
   * re-run — it should be *skipped*, with its dependents built on the real ground.
   *
   * The run is recorded as `done` with its source named in place of a reply, so the inspector shows
   * where the numbers came from rather than presenting measured data as a model's opinion. Anything
   * downstream is reset to pending, exactly as re-running the stage would: an artifact that changed
   * invalidates its dependents no matter who produced it.
   *
   * `detail` takes the place of the raw reply, and it is where an importer should put its evidence —
   * which records it kept, which it discarded and why, and where every number came from. "Why is
   * there a shed there" is the same question whether a model or a survey answered it, so it is
   * answered in the same pane rather than behind a second view nobody opens.
   */
  setArtifact(id: PipelineStageId, artifact: unknown, source: string, detail?: string): void {
    const index = PIPELINE_STAGE_IDS.indexOf(id);
    if (index < 0 || this.running) return;

    this.patch(id, {
      status: 'done',
      artifact,
      raw: detail ?? `Supplied by ${source} — this stage was not sent to a model.`,
      model: source,
      prompt: undefined,
      usage: undefined,
      durationMs: undefined,
      error: undefined,
    });
    for (const downstream of PIPELINE_STAGE_IDS.slice(index + 1)) {
      if (this.stages.find((stage) => stage.id === downstream)?.status === 'done') {
        this.patch(downstream, { status: 'pending', error: undefined });
      }
    }
  }

  /** Whether a stage's artifact came from somewhere other than a model. */
  isSupplied(id: PipelineStageId): boolean {
    const stage = this.stages.find((entry) => entry.id === id);
    return stage?.status === 'done' && stage.prompt === undefined;
  }

  /** Clear every stage back to pending — the "start over" path. */
  reset(): void {
    this.stages = PIPELINE_STAGE_IDS.map((id) => ({ id, status: 'pending' }));
    this.emit();
  }

  private async execute(queue: readonly PipelineStageId[]): Promise<void> {
    const first = queue[0];
    if (first === undefined) return;
    const apiKey = this.deps.apiKey();
    if (!apiKey) {
      this.patch(first, {
        status: 'failed',
        error: 'Add an OpenAI API key first.',
      });
      return;
    }
    if (this.deps.images().length === 0) {
      this.patch(first, {
        status: 'failed',
        error: 'Add at least one reference photograph.',
      });
      return;
    }
    this.running = true;
    this.abort = new AbortController();
    this.emit();

    try {
      for (const id of queue) {
        const ok = await this.runOne(id, apiKey);
        // A stage's output is the next stage's input, so continuing past a failure would ask the
        // next model to reason from nothing and quietly produce a plausible, unfounded answer.
        if (!ok) break;
      }
    } finally {
      this.running = false;
      this.abort = null;
      this.emit();
    }
  }

  private async runOne(id: PipelineStageId, apiKey: string): Promise<boolean> {
    const prompt = this.previewPrompt(id);
    const startedAt = this.time();
    this.patch(id, { status: 'running', prompt, error: undefined, raw: undefined, artifact: undefined });

    let call: VisionCallResult;
    try {
      call = await (this.deps.call ?? callVisionModel)({
        apiKey,
        prompt,
        images: this.deps.images(),
        model: this.deps.model(),
        maxOutputTokens: STAGE_OUTPUT_TOKENS[id],
        ...(this.abort ? { signal: this.abort.signal } : {}),
      });
    } catch (error) {
      const cancelled = error instanceof DOMException && error.name === 'AbortError';
      this.patch(id, {
        status: cancelled ? 'cancelled' : 'failed',
        durationMs: this.time() - startedAt,
        error: cancelled ? 'Cancelled.' : describeError(error),
      });
      return false;
    }

    // The raw text is recorded before parsing, so a stage that returned unreadable JSON still shows
    // what it actually said — which is nearly always enough to see why it was unreadable.
    const durationMs = this.time() - startedAt;
    try {
      const artifact = parseStageArtifact(id, JSON.parse(extractJson(call.raw)));
      // Materials the stage asked for are made *before* it is marked done, so that everything
      // downstream — the compiler, the inspector's overlay, the coverage readout — sees a palette
      // that already contains every material the artifact refers to.
      const materials =
        id === 'surfaces' ? await this.forgeMaterials(artifact as SurfaceArtifact, apiKey) : [];
      this.patch(id, {
        status: 'done',
        raw: call.raw,
        artifact,
        model: call.model,
        durationMs,
        ...(materials.length > 0 ? { materials } : {}),
        ...(call.usage ? { usage: call.usage } : {}),
      });
      return true;
    } catch (error) {
      this.patch(id, {
        status: 'failed',
        raw: call.raw,
        model: call.model,
        durationMs,
        ...(call.usage ? { usage: call.usage } : {}),
        error:
          error instanceof SitePlanFormatError
            ? `Could not read this stage's output: ${error.message}`
            : 'This stage did not return valid JSON.',
      });
      return false;
    }
  }

  /**
   * Make the materials the surfaces stage declared, and point its regions at what was made.
   *
   * Sequential rather than parallel, deliberately. Each forge is two API calls and the palette
   * assigns channels as it goes, so running them at once would race for the last free slot and make
   * "which material got dropped" depend on network timing. Six materials is the ceiling and a site
   * rarely needs two, so the latency this costs is small next to the confusion it avoids.
   *
   * A failure is per-material and never fails the stage: the regions that named it keep pointing at
   * an id nothing owns, and the compiler drops exactly those with a warning that names the material.
   * Losing one surface treatment is much better than losing a reconstruction that otherwise worked.
   */
  private async forgeMaterials(
    artifact: SurfaceArtifact,
    apiKey: string,
  ): Promise<ForgedMaterialReport[]> {
    const requests = artifact.materials ?? [];
    if (requests.length === 0) return [];
    if (this.deps.forgeMaterials?.() === false) {
      return requests.map((request) => ({
        requestedId: request.id,
        name: request.name,
        error: 'Skipped — new ground materials are turned off for this run.',
      }));
    }

    const forge = this.deps.forge ?? forgeTexture;
    const reports: ForgedMaterialReport[] = [];

    for (const request of requests) {
      try {
        const forged = await forge({
          request: request.request,
          apiKey,
          ...(request.context ? { context: request.context } : {}),
          ...(this.abort ? { signal: this.abort.signal } : {}),
        });
        // The palette owns ids — it slugifies and de-duplicates — so the artifact is rewritten to
        // refer to the material that exists rather than to the one the model imagined.
        for (const region of artifact.regions) {
          if (region.material === request.id) region.material = forged.layer.id;
        }
        reports.push({
          requestedId: request.id,
          layerId: forged.layer.id,
          name: forged.layer.name,
          color: forged.layer.color,
          detail: `${forged.recipeSource}, ${forged.swatchSource}`,
        });
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') throw error;
        reports.push({
          requestedId: request.id,
          name: request.name,
          error:
            error instanceof SurfacePaletteFullError
              ? error.message
              : `Could not make this material: ${describeError(error)}`,
        });
      }
    }

    return reports;
  }

  private patch(id: PipelineStageId, changes: StagePatch): void {
    this.stages = this.stages.map((stage) =>
      stage.id === id ? pruneUndefined({ ...stage, ...changes }) : stage,
    );
    this.emit();
  }

  private emit(): void {
    for (const listener of this.listeners) listener();
  }

  private time(): number {
    return (this.deps.now ?? defaultNow)();
  }
}

function defaultNow(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

/**
 * Drop keys explicitly set to `undefined`.
 *
 * `patch` uses `undefined` to mean "clear this", which spreads into a present-but-undefined key —
 * enough to trip `exactOptionalPropertyTypes` and to make the state object misleading in a debugger.
 */
function pruneUndefined(stage: StagePatch): StageRun {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(stage)) {
    if (value !== undefined) out[key] = value;
  }
  return out as unknown as StageRun;
}

function describeError(error: unknown): string {
  if (error instanceof SitePlanError) return error.message;
  if (error instanceof Error) return error.message;
  return 'Something went wrong running this stage.';
}
